import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AddRounded,
  AccessTimeRounded,
  AutoAwesomeOutlined,
  Brightness4Outlined,
  Brightness7Outlined,
  DashboardRounded,
  DescriptionOutlined,
  FolderSpecialOutlined,
  GroupsOutlined,
  ManageSearchRounded,
  MenuRounded,
  NotificationsNoneRounded,
  SearchRounded,
  SettingsOutlined,
  TaskAltRounded,
} from '@mui/icons-material';
import Logo from './Logo';
import WorkspaceErrorBoundary from './WorkspaceErrorBoundary';
import { ColorModeContext } from '../ColorModeContext';
import { getCurrentUser, logout } from '../features/account/api';
import { ACTIVE_MATTER_KEY, USER_SESSION_KEY, editionConfig, firmDisplayName, isNeoLegalFirm } from '../edition';

const drawerWidth = 272;
const appbarHeight = 72;

export const navSections = [
  {
    label: 'Overview',
    items: [
      { label: 'Command centre', icon: <DashboardRounded />, path: '/dashboard' },
      { label: 'Practice', icon: <AccessTimeRounded />, path: '/practice' },
    ],
  },
  {
    label: 'Matter lifecycle',
    items: [
      { label: 'Client intake', icon: <GroupsOutlined />, path: '/intake' },
      { label: 'Matter workspace', icon: <FolderSpecialOutlined />, path: '/matters' },
      { label: 'Practice playbook', icon: <TaskAltRounded />, path: '/playbooks' },
      { label: 'Case research', icon: <ManageSearchRounded />, path: '/research' },
      { label: 'Controlled drafting', icon: <DescriptionOutlined />, path: '/drafting' },
    ],
  },
  {
    label: 'Assistance',
    items: [
      { label: 'Matter copilot', icon: <AutoAwesomeOutlined />, path: '/copilot' },
    ],
  },
];

const routeNames = navSections
  .flatMap(section => section.items)
  .reduce((acc, item) => ({ ...acc, [item.path]: item.label }), { '/settings': 'Settings' });

export default function AppLayout() {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [userAnchor, setUserAnchor] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [apiOnline, setApiOnline] = useState(null);
  const [identity, setIdentity] = useState(null);
  const notifications = [];
  const organizationName = identity?.organizationName || '';
  const workspaceName = firmDisplayName(organizationName);
  const neoWorkspace = isNeoLegalFirm(organizationName);

  useEffect(() => {
    document.title = `${workspaceName} · ${editionConfig.platformName}`;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [workspaceName]);

  useEffect(() => {
    getCurrentUser()
      .then(body => setIdentity(body.user || identity))
      .catch(() => {});
    // Identity is refreshed when the workspace opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initials = String(identity?.name || 'User').split(/\s+/).filter(Boolean).map(part => part[0]).join('').slice(0, 2).toUpperCase();
  const roleLabel = identity?.professionalTitle || String(identity?.role || 'member').replace(/^./, value => value.toUpperCase());

  async function signOut() {
    try { await logout(); } catch { /* The local session is cleared below. */ }
    sessionStorage.removeItem(USER_SESSION_KEY);
    localStorage.removeItem(ACTIVE_MATTER_KEY);
    setUserAnchor(null);
    navigate('/', { replace: true });
  }

  useEffect(() => {
    const handleShortcut = event => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  useEffect(() => {
    fetch('/health')
      .then(response => { if (!response.ok) throw new Error('offline'); return response.json(); })
      .then(() => setApiOnline(true))
      .catch(() => setApiOnline(false));
  }, []);

  const goToSearch = event => {
    if (event.key === 'Enter' && search.trim()) {
      navigate(`/research?view=decisions&q=${encodeURIComponent(search.trim())}`);
      setSearch('');
    }
  };

  const contextualPath = path => {
    const activeMatterId = localStorage.getItem(ACTIVE_MATTER_KEY);
    if (!activeMatterId) return path;
    const encoded = encodeURIComponent(activeMatterId);
    if (path === '/matters') return `/matters?id=${encoded}`;
    if (path === '/playbooks') return `/playbooks?matterId=${encoded}`;
    if (path === '/drafting') return `/drafting?matterId=${encoded}`;
    return path;
  };

  const drawer = (
    <Box sx={{ width: drawerWidth, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#0B2135', color: '#fff' }}>
      <Box sx={{ px: 2.5, height: appbarHeight, display: 'flex', alignItems: 'center' }}>
        <Box onClick={() => navigate('/dashboard')} sx={{ display: 'flex', alignItems: 'center', gap: 1.35, cursor: 'pointer' }}>
          <Box sx={{ width: 38, height: 38, display: 'grid', placeItems: 'center', borderRadius: 2.5, bgcolor: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.1)' }}>
            <Logo variant="inverted" size={26} brand={neoWorkspace ? 'firm' : 'platform'} firmName={organizationName} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ maxWidth: 172, fontSize: 18, fontWeight: 780, lineHeight: 1.1, letterSpacing: '-.02em' }}>{workspaceName}</Typography>
            <Typography noWrap sx={{ maxWidth: 172, fontSize: 10, color: 'rgba(255,255,255,.55)', mt: 0.35, letterSpacing: '.07em', textTransform: 'uppercase' }}>{identity?.organizationJurisdiction || 'Secure firm workspace'}</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 2.25, pt: 1.25, pb: 1.5 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddRounded />}
          onClick={() => { navigate('/intake'); if (!desktop) setMobileOpen(false); }}
          sx={{ justifyContent: 'flex-start', bgcolor: '#fff', color: '#102A43', '&:hover': { bgcolor: '#E8F5F3' } }}
        >
          New client matter
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.25 }}>
        {navSections.map(section => (
          <Box key={section.label} sx={{ mb: 2 }}>
            <Typography variant="overline" sx={{ display: 'block', px: 1.5, mb: 0.55, color: 'rgba(255,255,255,.4)' }}>
              {section.label}
            </Typography>
            <List disablePadding>
              {section.items.map(item => {
                const selected = location.pathname.toLowerCase() === item.path.toLowerCase();
                return (
                  <ListItemButton
                    key={item.path}
                    selected={selected}
                    onClick={() => { navigate(contextualPath(item.path)); if (!desktop) setMobileOpen(false); }}
                    sx={{
                      mb: 0.45,
                      minHeight: 44,
                      borderRadius: 2.25,
                      color: selected ? '#fff' : 'rgba(255,255,255,.72)',
                      '& .MuiListItemIcon-root': { color: selected ? '#67D6CA' : 'rgba(255,255,255,.52)' },
                      '&.Mui-selected': { bgcolor: 'rgba(255,255,255,.10)', boxShadow: 'inset 3px 0 0 #36B8AC' },
                      '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,.13)' },
                      '&:hover': { bgcolor: 'rgba(255,255,255,.065)', color: '#fff' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 38, '& svg': { fontSize: 20 } }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13.5, fontWeight: selected ? 700 : 560 }} />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2 }}>
        <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,.055)', border: '1px solid rgba(255,255,255,.07)' }}>
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: apiOnline === false ? '#F59E0B' : '#39C6A7', boxShadow: `0 0 0 4px ${apiOnline === false ? 'rgba(245,158,11,.12)' : 'rgba(57,198,167,.12)'}` }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{apiOnline === false ? 'API needs attention' : 'Workspace online'}</Typography>
              <Typography sx={{ fontSize: 10.5, color: 'rgba(255,255,255,.48)' }} noWrap>{identity?.organizationName || editionConfig.platformName} · {identity?.organizationJurisdiction || 'Private workspace'}</Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );

  const currentRoute = routeNames[location.pathname.toLowerCase()] || 'Workspace';

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          height: appbarHeight,
          bgcolor: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          zIndex: theme.zIndex.drawer - 1,
        }}
      >
        <Toolbar sx={{ minHeight: `${appbarHeight}px !important`, px: { xs: 1.25, sm: 2.5 }, gap: 1.5 }}>
          {!desktop && (
            <IconButton aria-label="Open navigation" onClick={() => setMobileOpen(true)}><MenuRounded /></IconButton>
          )}

          <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 145 }}>
            <Typography variant="caption" color="text.secondary">Workspace</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 750, lineHeight: 1.25, textTransform: 'capitalize' }}>{currentRoute}</Typography>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <TextField
              inputRef={searchRef}
              value={search}
              onChange={event => setSearch(event.target.value)}
              onKeyDown={goToSearch}
              placeholder="Search Canadian authorities…"
              size="small"
              inputProps={{ 'aria-label': 'Search workspace' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchRounded sx={{ fontSize: 20, color: 'text.secondary' }} /></InputAdornment>,
                endAdornment: <InputAdornment position="end"><Chip label="⌘ K" size="small" sx={{ height: 22, display: { xs: 'none', lg: 'flex' }, bgcolor: 'background.default', color: 'text.secondary', fontSize: 10 }} /></InputAdornment>,
              }}
              sx={{ width: '100%', maxWidth: 560, '& .MuiOutlinedInput-root': { height: 42, bgcolor: 'background.default' } }}
            />
          </Box>

          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Tooltip title={colorMode.mode === 'dark' ? 'Use light mode' : 'Use dark mode'}>
              <IconButton aria-label="Toggle colour mode" onClick={colorMode.toggleColorMode} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                {colorMode.mode === 'dark' ? <Brightness7Outlined /> : <Brightness4Outlined />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Notifications">
              <IconButton aria-label="Notifications" onClick={event => setNotificationAnchor(event.currentTarget)}>
                <Badge variant="dot" color="error" invisible={!notifications.length}><NotificationsNoneRounded /></Badge>
              </IconButton>
            </Tooltip>
            <IconButton aria-label="Account menu" onClick={event => setUserAnchor(event.currentTarget)} sx={{ ml: 0.5 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 13, fontWeight: 800 }}>{initials}</Avatar>
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer variant="permanent" open sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: drawerWidth, border: 0 } }}>{drawer}</Drawer>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth, border: 0 } }}>{drawer}</Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, height: `calc(100vh - ${appbarHeight}px)`, mt: `${appbarHeight}px`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, sm: 2.5, lg: 3.5 } }}>
          <WorkspaceErrorBoundary resetKey={`${location.pathname}${location.search}`}>
            <Outlet />
          </WorkspaceErrorBoundary>
        </Box>
      </Box>

      <Menu anchorEl={notificationAnchor} open={Boolean(notificationAnchor)} onClose={() => setNotificationAnchor(null)} PaperProps={{ sx: { width: 330, mt: 1 } }}>
        <Box sx={{ px: 2, py: 1.25 }}><Typography variant="subtitle1">Notifications</Typography><Typography variant="caption" color="text.secondary">Current verified workspace alerts</Typography></Box>
        <Divider />
        {notifications.length
          ? notifications.map(item => <MenuItem key={item.id} sx={{ py: 1.25, whiteSpace: 'normal', fontSize: 13 }}>{item.label}</MenuItem>)
          : <Box sx={{ px: 2, py: 2.5 }}><Typography variant="body2" color="text.secondary">No verified alerts right now.</Typography></Box>}
      </Menu>

      <Menu anchorEl={userAnchor} open={Boolean(userAnchor)} onClose={() => setUserAnchor(null)} PaperProps={{ sx: { width: 205, mt: 1 } }}>
        <Box sx={{ px: 2, py: 1.25 }}><Typography sx={{ fontWeight: 750, fontSize: 14 }}>{identity?.name}</Typography><Typography variant="caption" color="text.secondary">{roleLabel} · {identity?.organizationName}</Typography></Box>
        <Divider />
        <MenuItem onClick={() => { setUserAnchor(null); navigate('/practice'); }}><AccessTimeRounded sx={{ mr: 1.25, fontSize: 19 }} /> Practice</MenuItem>
        <MenuItem onClick={() => { setUserAnchor(null); navigate('/settings'); }}><SettingsOutlined sx={{ mr: 1.25, fontSize: 19 }} /> Settings</MenuItem>
        <MenuItem onClick={signOut}>Sign out</MenuItem>
      </Menu>
    </Box>
  );
}
